import { isSubResourcePricingEnabled } from '../../utils/subResourcePricing';
import { parseQuantityUnitFromBillConfig } from '../quantityUnit/parse';
import type { VendorDeviceBillOption, VendorDeviceCompositionItem, VendorDeviceSelection, VendorDeviceSubResourceConfig } from './types';

interface RawBillItemRecord {
  id?: string;
  code?: string;
  name?: string;
  deviceInfo?: {
    vendor?: string;
    device?: string;
    vendorID?: string;
    deviceID?: string;
    deviceType?: string;
    detail?: string;
  };
  resource?: {
    code?: string;
    unit?: string;
  };
}

interface RawBillModelRelationRecord {
  id?: string;
  config?: string;
  billType?: string;
  useSubResourcePricing?: boolean;
  subResourceConfigs?: VendorDeviceSubResourceConfig[];
}

interface RawResolvedBillItemRecord {
  vendorId?: string;
  deviceId?: string;
  billItem?: RawBillItemRecord;
  relation?: RawBillModelRelationRecord;
}

function readString(value: unknown): string {
  return String(value ?? '').trim();
}

export const GENERIC_VENDOR_DEVICE_LABEL = '通用';

/** 后端通用厂商/设备占位码，展示时映射为「通用」 */
export const GENERIC_VENDOR_DEVICE_CODES = ['GENERIC_VENDOR', 'GENERIC_DEVICE'] as const;

/** 列表展示：空值或通用占位码显示「通用」 */
export function formatVendorDeviceCellLabel(value?: string): string {
  const text = readString(value);
  if (!text || (GENERIC_VENDOR_DEVICE_CODES as readonly string[]).includes(text)) {
    return GENERIC_VENDOR_DEVICE_LABEL;
  }
  return text;
}

function unwrapBillItemsResponse(res: unknown): unknown[] {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object' && Array.isArray((res as { items?: unknown[] }).items)) {
    return (res as { items: unknown[] }).items;
  }
  return [];
}

function resolveOptionId(
  vendorId: string,
  deviceId: string,
  billItemId: string,
  relationId: string,
  useSubResourcePricing: boolean,
): string {
  const pricingSuffix = useSubResourcePricing ? 'sub' : 'total';
  if (billItemId) return `${billItemId}::${pricingSuffix}`;
  if (vendorId || deviceId) return `${vendorId}::${deviceId}::${pricingSuffix}`;
  if (relationId) return `${relationId}::${pricingSuffix}`;
  return `unknown::${pricingSuffix}`;
}

/** 选项是否子资源计价（与 optionToSelection 写入态一致） */
export function resolveOptionUseSubResourcePricing(
  option: Pick<VendorDeviceBillOption, 'useSubResourcePricing' | 'subResourceConfigs'>,
): boolean {
  return isSubResourcePricingEnabled(option);
}

function normalizeUseSubResourcePricing(value: unknown): boolean {
  return value === true;
}

/**
 * 回填选中：vendorID + deviceID + useSubResourcePricing 三者共同确认
 * （同一厂商/设备可能同时存在总量与子资源两套计费项；未标记 useSubResourcePricing 视为总量计价）
 */
export function isVendorDeviceOptionMatch(option: VendorDeviceBillOption, selection: VendorDeviceSelection): boolean {
  const vendorID = readString(selection.vendorID);
  const deviceID = readString(selection.deviceID);
  if (!(vendorID || deviceID)) return false;
  if (readString(option.vendorID) !== vendorID || readString(option.deviceID) !== deviceID) return false;
  return resolveOptionUseSubResourcePricing(option) === normalizeUseSubResourcePricing(selection.useSubResourcePricing);
}

/** 从选项列表按 vendorID + deviceID + useSubResourcePricing 匹配选中项 */
export function findVendorDeviceOption(
  options: VendorDeviceBillOption[],
  selection?: VendorDeviceSelection | null,
): VendorDeviceBillOption | undefined {
  if (!selection) return undefined;
  const vendorID = readString(selection.vendorID);
  const deviceID = readString(selection.deviceID);
  if (!(vendorID || deviceID)) return undefined;
  return options.find((item) => isVendorDeviceOptionMatch(item, selection));
}

/** 解析 detail JSON 字符串，提取组合资源列表 */
function parseDetailComposition(detail?: string): VendorDeviceCompositionItem[] | undefined {
  if (!detail) return undefined;
  try {
    const parsed = JSON.parse(detail);
    const items = Array.isArray(parsed) ? parsed : [];
    const first = items[0];
    if (!first || !Array.isArray(first.composition)) return undefined;
    return first.composition as VendorDeviceCompositionItem[];
  } catch {
    return undefined;
  }
}

/** 计费 GPU detail 中解析出的驱动 / 软件栈（写入表单 driverVersion / cudaVersion） */
export interface BillDeviceGpuSoftwareInfo {
  driverVersion?: string;
  cudaVersion?: string;
  /** quantity.resourceName，容器 GPU 写入 card.name */
  resourceName?: string;
  code?: string;
  resourceType?: string;
}

function readSoftwareVersions(
  softwareItem: Record<string, unknown> | undefined,
): Pick<BillDeviceGpuSoftwareInfo, 'driverVersion' | 'cudaVersion'> {
  if (!softwareItem) return {};
  const driverVersion = readString(softwareItem.DriverVersion ?? softwareItem.driverVersion) || undefined;
  const cudaVersion = readString(softwareItem.CUDAVersion ?? softwareItem.cudaVersion) || undefined;
  return {
    ...(driverVersion ? { driverVersion } : {}),
    ...(cudaVersion ? { cudaVersion } : {}),
  };
}

/**
 * 解析 billItem.deviceInfo.detail（库存匹配项数组）中的 GPU 软件栈。
 * 兼容：`extraInfo.software` / 顶层 `software` / `gpu.software`；字段 `DriverVersion`/`CUDAVersion` 或小写。
 */
export function parseGpuSoftwareFromBillDeviceDetail(detail?: string): BillDeviceGpuSoftwareInfo | undefined {
  if (!detail) return undefined;
  try {
    const parsed = JSON.parse(detail) as unknown;
    const first = (Array.isArray(parsed) ? parsed[0] : parsed) as Record<string, unknown> | null | undefined;
    if (!first || typeof first !== 'object') return undefined;

    const extraInfo = first.extraInfo && typeof first.extraInfo === 'object' ? (first.extraInfo as Record<string, unknown>) : undefined;
    const gpuBlock = first.gpu && typeof first.gpu === 'object' ? (first.gpu as Record<string, unknown>) : undefined;
    const softwareList = [extraInfo?.software, first.software, gpuBlock?.software].find((item) => Array.isArray(item)) as
      | Record<string, unknown>[]
      | undefined;
    const firstSoftware = softwareList?.find((item) => item && typeof item === 'object');
    const versions = readSoftwareVersions(firstSoftware);

    const quantity = first.quantity && typeof first.quantity === 'object' ? (first.quantity as Record<string, unknown>) : undefined;
    const resourceName = readString(quantity?.resourceName) || undefined;
    const code = readString(first.code) || undefined;
    const resourceType = readString(first.resourceType) || undefined;

    if (!versions.driverVersion && !versions.cudaVersion && !resourceName && !code && !resourceType) {
      return undefined;
    }
    return {
      ...versions,
      ...(resourceName ? { resourceName } : {}),
      ...(code ? { code } : {}),
      ...(resourceType ? { resourceType } : {}),
    };
  } catch {
    return undefined;
  }
}

/** 解析 resolve 接口响应为选项列表 */
export function normalizeVendorDeviceBillItems(res: unknown, resourceCode: string): VendorDeviceBillOption[] {
  return unwrapBillItemsResponse(res)
    .filter((item): item is RawResolvedBillItemRecord => !!item && typeof item === 'object')
    .map((item) => {
      const billItem = item.billItem ?? {};
      const relation = item.relation ?? {};
      const deviceInfo = billItem.deviceInfo ?? {};
      const vendorId = readString(item.vendorId ?? deviceInfo.vendorID);
      const deviceId = readString(item.deviceId ?? deviceInfo.deviceID);
      const vendor = readString(deviceInfo.vendor);
      const device = readString(deviceInfo.device);
      const billItemId = readString(billItem.id);
      const relationId = readString(relation.id);
      const detail = readString(deviceInfo.detail) || undefined;

      const useSubResourcePricing = Boolean(relation.useSubResourcePricing);
      const subResourceConfigs = Array.isArray(relation.subResourceConfigs) ? relation.subResourceConfigs : undefined;
      const option: VendorDeviceBillOption = {
        id: resolveOptionId(
          vendorId,
          deviceId,
          billItemId,
          relationId,
          isSubResourcePricingEnabled({ useSubResourcePricing, subResourceConfigs }),
        ),
        billItemId: billItemId || undefined,
        resourceCode,
        vendor,
        device,
        vendorID: vendorId || undefined,
        deviceID: deviceId || undefined,
        deviceType: readString(deviceInfo.deviceType) || undefined,
        billConfig: relation.config,
        billType: readString(relation.billType) || undefined,
        useSubResourcePricing,
        subResourceConfigs,
        resourceUnit: readString(billItem.resource?.unit) || undefined,
        detail,
        composition: parseDetailComposition(detail),
      };
      return option;
    })
    .filter((item) => item.billItemId || item.vendorID || item.deviceID || item.vendor || item.device);
}

export function hasVendorDeviceSelection(selection?: VendorDeviceSelection | null): boolean {
  if (!selection) return false;
  return !!(
    selection.billItemId?.trim() ||
    selection.vendorID?.trim() ||
    selection.deviceID?.trim() ||
    selection.vendor?.trim() ||
    selection.device?.trim()
  );
}

/** 按设备类型筛选厂商设备计费项；未指定类型时保持完整列表。 */
export function filterVendorDeviceOptionsByDeviceType(options: VendorDeviceBillOption[], deviceType?: string): VendorDeviceBillOption[] {
  const expected = readString(deviceType).toLowerCase();
  if (!expected) return options;
  return options.filter((option) => {
    const optionDeviceType = readString(option.deviceType).toLowerCase();
    return !optionDeviceType || optionDeviceType === expected;
  });
}

export function selectionToOptionKey(selection?: VendorDeviceSelection | null): string | undefined {
  if (!selection || !hasVendorDeviceSelection(selection)) return undefined;
  const pricingSuffix = normalizeUseSubResourcePricing(selection.useSubResourcePricing) ? 'sub' : 'total';
  const vendorID = readString(selection.vendorID);
  const deviceID = readString(selection.deviceID);
  if (vendorID || deviceID) return `${vendorID}::${deviceID}::${pricingSuffix}`;
  const vendor = readString(selection.vendor);
  const device = readString(selection.device);
  if (!vendor && !device) return undefined;
  return `${vendor}::${device}::${pricingSuffix}`;
}

export function optionToSelection(option: VendorDeviceBillOption): VendorDeviceSelection {
  const quantityUnit =
    parseQuantityUnitFromBillConfig(option.billConfig) ||
    (option.resourceUnit != null && String(option.resourceUnit).trim() !== '' ? String(option.resourceUnit).trim() : undefined);
  return {
    vendor: option.vendor,
    device: option.device,
    vendorID: option.vendorID,
    deviceID: option.deviceID,
    deviceType: option.deviceType,
    billItemId: option.billItemId,
    detail: option.detail,
    useSubResourcePricing: resolveOptionUseSubResourcePricing(option),
    ...(quantityUnit ? { quantityUnit } : {}),
    ...(option.matchedResourceConfiguration ? { matchedResourceConfiguration: option.matchedResourceConfiguration } : {}),
  };
}

/**
 * 将已选/预置选型合成列表行（固定规格下单 skipFetch 时不请求 API，仅展示预置）。
 * composition / 参考价依赖 selection 上携带的 detail、billConfig、subResourceConfigs。
 */
export function selectionToBillOption(selection: VendorDeviceSelection, resourceCode?: string): VendorDeviceBillOption | undefined {
  if (!hasVendorDeviceSelection(selection)) return undefined;
  const vendorID = readString(selection.vendorID);
  const deviceID = readString(selection.deviceID);
  const billItemId = readString(selection.billItemId);
  const useSubResourcePricing = normalizeUseSubResourcePricing(selection.useSubResourcePricing);
  const detail = readString(selection.detail) || undefined;
  const enriched = selection as VendorDeviceSelection &
    Partial<Pick<VendorDeviceBillOption, 'billConfig' | 'billType' | 'subResourceConfigs' | 'composition' | 'resourceUnit'>>;
  const quantityUnit = readString(selection.quantityUnit) || undefined;

  return {
    id: resolveOptionId(vendorID, deviceID, billItemId, '', useSubResourcePricing),
    billItemId: billItemId || undefined,
    resourceCode,
    vendor: readString(selection.vendor),
    device: readString(selection.device),
    vendorID: vendorID || undefined,
    deviceID: deviceID || undefined,
    deviceType: readString(selection.deviceType) || undefined,
    billConfig: enriched.billConfig,
    billType: enriched.billType,
    useSubResourcePricing,
    subResourceConfigs: enriched.subResourceConfigs,
    resourceUnit: enriched.resourceUnit || quantityUnit,
    detail,
    composition: enriched.composition ?? parseDetailComposition(detail),
  };
}

/** skipFetch 时解析展示用选项：优先 presetOptions，否则从 value 合成 */
export function resolveSkipFetchOptions(
  value: VendorDeviceSelection | null | undefined,
  resourceCode: string,
  presetOptions?: VendorDeviceBillOption[],
): VendorDeviceBillOption[] {
  if (presetOptions?.length) return presetOptions;
  const fromValue = value ? selectionToBillOption(value, resourceCode) : undefined;
  return fromValue ? [fromValue] : [];
}

export { unwrapBillItemsResponse };
